import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, Lines } from "../components/site/Motion";
import { IMG } from "../lib/images";
import { GUIDE_CATEGORIES, categoryMeta } from "../lib/guideCategories";

export default function GuidesPage() {
  const [guides, setGuides] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    document.title = "SWAPA Guide — Guide alla microcar e alla mobilità urbana";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "SWAPA Guide: la piattaforma di conoscenza su microcar, minicar, patente AM, categoria L6e, mobilità elettrica, sicurezza, costi e vita urbana. Guide verificate e aggiornate.");
    api.get("/guides").then((r) => setGuides(r.data)).catch(() => {});
  }, []);

  const pillars = guides.filter((g) => g.pillar);
  const articles = guides.filter((g) => !g.pillar && (cat === "all" || g.category === cat));
  const usedCats = GUIDE_CATEGORIES.filter((c) => guides.some((g) => g.category === c.key));

  return (
    <div data-testid="guides-page" className="bg-ink pb-28 pt-32 sm:pt-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow">SWAPA Guide</p>
        <h1 className="mt-6 font-display font-black uppercase leading-[0.9] tracking-tighter">
          <Lines animate="load" lines={["Guide alla microcar", "e alla mobilità urbana."]} className="text-4xl text-white sm:text-6xl lg:text-8xl" />
        </h1>
        <Reveal delay={0.25}>
          <p className="mt-8 max-w-2xl font-serif text-lg italic leading-relaxed text-zinc-400">
            SWAPA Guide è la piattaforma di conoscenza di SWAPA: guide verificate e aggiornate per capire
            davvero microcar e minicar, patente AM e categoria L6e, mobilità elettrica, sicurezza, costi
            di gestione e uso quotidiano in città. Prima le risposte, poi il prodotto.
          </p>
        </Reveal>

        {pillars.length > 0 && (
          <section className="mt-20" data-testid="guides-pillars">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Le guide fondamentali</p>
            <div className="mt-8 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
              {pillars.map((g, i) => (
                <Reveal key={g.slug} delay={i * 0.08} className="bg-ink">
                  <Link to={`/guide/${g.slug}`} data-testid={`pillar-card-${g.slug}`} className="group flex h-full flex-col p-8">
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-swapa-yellow">{categoryMeta(g.category).label}</p>
                    <h2 className="mt-4 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-white transition-colors group-hover:text-swapa-yellow sm:text-3xl">
                      {g.title}
                    </h2>
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-zinc-400">{g.excerpt}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 transition-colors group-hover:text-white">
                      Leggi la guida completa <ArrowUpRight className="h-3.5 w-3.5" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        <section className="mt-24" data-testid="guides-articles">
          <div className="flex flex-wrap gap-2" data-testid="guides-filters">
            <button onClick={() => setCat("all")} data-testid="filter-all"
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${cat === "all" ? "border-swapa-yellow bg-swapa-yellow text-black" : "border-white/15 text-zinc-300 hover:border-swapa-yellow hover:text-swapa-yellow"}`}>
              Tutte
            </button>
            {usedCats.map((c) => (
              <button key={c.key} onClick={() => setCat(c.key)} data-testid={`filter-${c.key}`}
                className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${cat === c.key ? "border-swapa-yellow bg-swapa-yellow text-black" : "border-white/15 text-zinc-300 hover:border-swapa-yellow hover:text-swapa-yellow"}`}>
                {c.label}
              </button>
            ))}
          </div>
          {cat !== "all" && (
            <p className="mt-6 max-w-xl text-sm italic leading-relaxed text-zinc-500">{categoryMeta(cat).intro}</p>
          )}

          <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {articles.map((g, i) => (
              <Reveal key={g.slug} delay={(i % 3) * 0.08}>
                <Link to={`/guide/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="group block h-full border border-white/10 bg-card transition-colors duration-500 hover:border-swapa-yellow/50">
                  <div className="overflow-hidden">
                    <img
                      src={g.hero_image || IMG.guideCity}
                      alt={g.title}
                      className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                      <span className="text-swapa-yellow">{categoryMeta(g.category).label}</span>
                      <span>{g.reading_time} min</span>
                    </div>
                    <h2 className="mt-3 font-display text-xl font-bold uppercase leading-tight tracking-tight text-white group-hover:text-swapa-yellow">
                      {g.title}
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-400">{g.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 group-hover:text-white">
                      Leggi la guida <ArrowUpRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
            {articles.length === 0 && (
              <p className="col-span-full py-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-600">
                Nessuna guida in questa categoria per ora.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
